import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import type { EntityContext } from "./EntityContext";
import { quoteEventsApi, type QuoteEventDto } from "@/lib/quote-events";
import { jobsApi, type JobEventDto } from "@/lib/jobs";

export interface EntityEventsTabProps {
  context: EntityContext;
}

interface EventRow {
  id: number;
  kind: string;
  fromStatus: string | null;
  toStatus: string | null;
  comment: string | null;
  occurredOnUtc: string;
  actorUserName: string;
}

function toRow(e: QuoteEventDto | JobEventDto): EventRow {
  return {
    id:            "quoteEventId" in e ? e.quoteEventId : e.jobEventId,
    kind:          e.eventKind,
    fromStatus:    e.fromStatusCode,
    toStatus:      e.toStatusCode,
    comment:       e.comment,
    occurredOnUtc: e.occurredOnUtc,
    actorUserName: e.actorUserName,
  };
}

export function EntityEventsTab({ context }: EntityEventsTabProps) {
  const { data = [], isLoading, isError } = useQuery<(QuoteEventDto | JobEventDto)[]>({
    queryKey: [context.kind, context.id, "events"],
    queryFn: async () =>
      context.kind === "quote"
        ? await quoteEventsApi.list(context.id)
        : await jobsApi.events(context.id),
  });

  if (isLoading) return <p className="p-4 text-sm text-muted-foreground">Loading events…</p>;
  if (isError)   return <p role="alert" className="p-4 text-sm text-destructive">Failed to load events.</p>;
  if (data.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          No events recorded yet.
        </CardContent>
      </Card>
    );
  }

  const rows = data.map(toRow);
  const fmtDate = (iso: string) => new Date(iso).toLocaleString("en-AU");
  return (
    <Card>
      <CardContent className="p-0">
        <ol className="divide-y divide-border">
          {rows.map((r) => (
            <li key={r.id} className="grid grid-cols-[160px_1fr] gap-3 px-4 py-3 text-sm">
              <div className="text-muted-foreground">{fmtDate(r.occurredOnUtc)}</div>
              <div>
                <div className="font-medium text-foreground">
                  {r.kind}
                  {r.fromStatus && r.toStatus && (
                    <span className="ml-2 text-muted-foreground">
                      ({r.fromStatus} → {r.toStatus})
                    </span>
                  )}
                </div>
                {r.comment && <div className="mt-1 text-muted-foreground">{r.comment}</div>}
                <div className="mt-1 text-xs text-muted-foreground">by {r.actorUserName}</div>
              </div>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}
